import { Component, Input, HostListener, inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'app-listing-gallery',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="gallery-container" *ngIf="images && images.length > 0">
      <div class="main-image-wrapper">
        <img [src]="images[selectedIndex]" (click)="openLightbox()" alt="Listing main image" class="main-image" loading="lazy">
        <button class="nav-arrow prev" (click)="prevImage($event)">&#10094;</button>
        <button class="nav-arrow next" (click)="nextImage($event)">&#10095;</button>
        <div class="image-counter">{{selectedIndex + 1}} / {{images.length}}</div>
      </div>

      <div class="thumbnails-container">
        <div class="thumbnail" *ngFor="let img of images; let i = index"
             [class.active]="i === selectedIndex" (click)="selectImage(i)">
          <img [src]="img" alt="Thumbnail" loading="lazy">
        </div>
      </div>

      <!-- Lightbox -->
      <div class="lightbox" *ngIf="isLightboxOpen" (click)="closeLightbox()">
        <button class="close-btn" (click)="closeLightbox()">&times;</button>
        <div class="lightbox-content" (click)="$event.stopPropagation()">
          <img [src]="images[selectedIndex]" alt="Lightbox image">
          <button class="nav-arrow prev" (click)="prevImage($event)">&#10094;</button>
          <button class="nav-arrow next" (click)="nextImage($event)">&#10095;</button>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .gallery-container {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }
    .main-image-wrapper {
      position: relative;
      aspect-ratio: 16 / 9;
      background: #f8f9fa;
      border-radius: 12px;
      overflow: hidden;
      cursor: pointer;
    }
    .main-image {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: opacity 0.3s ease;
    }
    .nav-arrow {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      background: rgba(0,0,0,0.5);
      color: white;
      border: none;
      width: 40px;
      height: 40px;
      border-radius: 50%;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 18px;
      z-index: 2;
      transition: background 0.3s;
    }
    .nav-arrow:hover { background: rgba(0,0,0,0.7); }
    .prev { left: 16px; }
    .next { right: 16px; }
    .image-counter {
      position: absolute;
      bottom: 16px;
      right: 16px;
      background: rgba(0,0,0,0.6);
      color: white;
      padding: 4px 12px;
      border-radius: 20px;
      font-size: 14px;
    }
    .thumbnails-container {
      display: flex;
      gap: 8px;
      overflow-x: auto;
      padding-bottom: 8px;
    }
    .thumbnail {
      width: 80px;
      height: 60px;
      border-radius: 8px;
      overflow: hidden;
      cursor: pointer;
      flex-shrink: 0;
      border: 2px solid transparent;
      transition: all 0.2s;
    }
    .thumbnail.active { border-color: #007bff; opacity: 1; }
    .thumbnail:not(.active) { opacity: 0.6; }
    .thumbnail img { width: 100%; height: 100%; object-fit: cover; }

    /* Lightbox */
    .lightbox {
      position: fixed;
      top: 0; left: 0; width: 100%; height: 100%;
      background: rgba(0,0,0,0.9);
      z-index: 1000;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .lightbox-content {
      max-width: 90%;
      max-height: 90%;
      position: relative;
    }
    .lightbox-content img {
      max-width: 100%;
      max-height: 90vh;
      object-fit: contain;
    }
    .close-btn {
      position: absolute;
      top: 24px;
      right: 24px;
      background: none;
      border: none;
      color: white;
      font-size: 48px;
      cursor: pointer;
      z-index: 1001;
    }
  `]
})
export class ListingGalleryComponent {
  private platformId = inject(PLATFORM_ID);
  @Input() images: string[] = [];
  selectedIndex = 0;
  isLightboxOpen = false;

  selectImage(index: number) {
    this.selectedIndex = index;
  }

  nextImage(event?: Event) {
    if (event) event.stopPropagation();
    this.selectedIndex = (this.selectedIndex + 1) % this.images.length;
  }

  prevImage(event?: Event) {
    if (event) event.stopPropagation();
    this.selectedIndex = (this.selectedIndex - 1 + this.images.length) % this.images.length;
  }

  openLightbox() {
    this.isLightboxOpen = true;
    if (isPlatformBrowser(this.platformId)) {
      document.body.style.overflow = 'hidden';
    }
  }

  closeLightbox() {
    this.isLightboxOpen = false;
    if (isPlatformBrowser(this.platformId)) {
      document.body.style.overflow = 'auto';
    }
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (!this.isLightboxOpen) return;
    if (event.key === 'ArrowRight') this.nextImage();
    if (event.key === 'ArrowLeft') this.prevImage();
    if (event.key === 'Escape') this.closeLightbox();
  }
}
